import { useState } from 'preact/hooks';
import type { CharacterSave } from '../../game/character';
import { countOf } from '../../game/inventory';
import type { QuestDef } from '../../game/quests';
import { abandon } from '../../game/quests';
import { ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { SHOPS } from '../economy/shops';
import { ShopView } from '../economy/ShopPanel';
import {
  Amount,
  Bar,
  Coin,
  Rewards,
  TipLayer,
  commit,
  duration,
  getGame,
  toast,
  useCtx,
  useNow,
} from '../economy/ui';
import {
  type BountyKind,
  REP_NAMES,
  REP_TIERS,
  bountyQuest,
  bountyTemplate,
  repTier,
} from './bounties';
import {
  acceptBounty,
  bountyContext,
  bountyState,
  claimBounty,
  restContext,
  restDay,
} from './board';
import { REST_CAP, REST_PER_RUN, dailyCore, nextRealReset, restStatus } from './dayCycle';

type Tab = BountyKind | 'seals';

const TAG_HUE: Record<string, string> = {
  Hunt: '#d0405a',
  Elite: '#e8793a',
  Boss: '#f4555a',
  Dungeon: '#6f9fd8',
  Gather: '#7bd66b',
  Explore: '#4fc2b0',
  Supplies: '#e3c35a',
  Forge: '#ff9a3c',
  Rift: '#b070ff',
  Colossus: '#ff9040',
  Board: '#d3b67a',
  Deepvault: '#40c0b0',
  Siege: '#ff4030',
};

function Stars({ n }: { n: number }) {
  return (
    <span class="bty-stars" aria-label={`Difficulty ${n} of 3`}>
      {[1, 2, 3].map((i) => (
        <i key={i} class={i <= n ? 'on' : ''} />
      ))}
    </span>
  );
}

function progressOf(
  c: CharacterSave,
  q: QuestDef,
  state: ReturnType<typeof bountyState>,
): { have: number; need: number } {
  const need = q.steps[0]?.objectives[0]?.count ?? 1;
  if (state === 'done' || state === 'ready') return { have: need, need };
  const pr = c.quests.active[q.id];
  return { have: Math.min(need, pr?.counts[0] ?? 0), need };
}

function showRewards(q: QuestDef): void {
  const r = q.rewards;
  ui.rewards.value = {
    title: q.name,
    subtitle: q.kind === 'weekly' ? 'Weekly bounty complete' : 'Bounty complete',
    silver: r.silver ?? 0,
    gold: r.gold ?? 0,
    items: r.items ?? [],
    equip: [],
    cards: [],
    xp: r.xp,
  };
}

function BountyCard({ id }: { id: string }) {
  const ctx = useCtx();
  if (!ctx) return null;
  const { p, c } = ctx;
  const q = bountyQuest(id, bountyContext());
  const t = bountyTemplate(id);
  if (!q || !t) return null;
  const state = bountyState(c, id);
  const obj = q.steps[0]?.objectives[0];
  const { have, need } = progressOf(c, q, state);
  const zone = obj?.zone ? bountyContext().zones.find((z) => z.id === obj.zone)?.name : undefined;
  const hue = TAG_HUE[t.tag] ?? '#d3b67a';
  return (
    <article class={`bty-card is-${state}`} style={{ ['--tag' as string]: hue }}>
      <div class="bty-card-top">
        <span class="bty-tag">{t.tag}</span>
        <Stars n={t.diff} />
        <span class="fx-spacer" />
        {state === 'done' && <span class="bty-stamp">Claimed</span>}
        {state === 'ready' && <span class="bty-stamp is-ready">Complete</span>}
      </div>
      <h3 class="bty-title">{q.name}</h3>
      <p class="bty-blurb">{q.dialogue.offer[0]}</p>
      <div class="bty-obj">
        <span class="bty-obj-t">
          {obj?.text}
          {zone && <span class="fx-dim"> · {zone}</span>}
        </span>
        <Bar
          value={have}
          max={need}
          tone={state === 'ready' || state === 'done' ? 'green' : 'red'}
          label={`${have}/${need}`}
        />
      </div>
      <div class="bty-card-foot">
        <Rewards
          xp={q.rewards.xp}
          silver={q.rewards.silver}
          gold={q.rewards.gold}
          items={q.rewards.items}
        />
        <span class="fx-spacer" />
        {state === 'offered' && (
          <button
            class="btn btn--gold"
            onClick={() => {
              if (acceptBounty(p, id)) commit();
            }}
          >
            Accept
          </button>
        )}
        {state === 'active' && (
          <button
            class="btn sm"
            onClick={() => {
              abandon(c, id);
              toast(`Bounty dropped: ${q.name}`);
              commit();
            }}
          >
            Abandon
          </button>
        )}
        {state === 'ready' && (
          <button
            class="btn btn--gold"
            onClick={() => {
              const done = claimBounty(p, id);
              if (done) {
                showRewards(done);
                commit();
              }
            }}
          >
            Claim
          </button>
        )}
      </div>
    </article>
  );
}

function DaySide() {
  const ctx = useCtx();
  const now = useNow(1000);
  const game = getGame();
  if (!ctx) return null;
  const { c, a } = ctx;
  const core = dailyCore(c);
  const rest = game ? restStatus(c, restContext(game)) : { ok: false, reason: '' };
  const session = a.settings.sessionDays;
  const endgame = core.some((t) => t.id === 'rift');
  return (
    <aside class="bty-side">
      <div class="bty-day">
        <span class="fx-h">Session day</span>
        <div class="bty-daynum">
          Day <b>{a.day}</b>
          <span class="fx-dim"> · Week {a.week}</span>
        </div>
        <div class="bty-mode">
          {session
            ? 'Days advance when you Rest'
            : `Real time · next reset in ${duration(nextRealReset(now) - now)}`}
        </div>
      </div>
      <div class="bty-block">
        <span class="fx-h">Daily core</span>
        <ul class="bty-core">
          {core.map((t) => (
            <li key={t.id} class={t.done ? 'is-done' : ''}>
              <i />
              <span>{t.label}</span>
              <span class="fx-dim">{t.detail}</span>
            </li>
          ))}
        </ul>
      </div>
      <div class="bty-block">
        <span class="fx-h">Rested bonus</span>
        <div class="bty-rest-row">
          <span>Rift Delve</span>
          <Bar
            value={c.daily.riftRested}
            max={REST_CAP}
            tone="violet"
            label={`${c.daily.riftRested}/${REST_CAP}`}
          />
        </div>
        <div class="bty-rest-row">
          <span>Colossus Hunt</span>
          <Bar
            value={c.daily.colossusRested}
            max={REST_CAP}
            tone="blue"
            label={`${c.daily.colossusRested}/${REST_CAP}`}
          />
        </div>
        <p class="bty-note">
          {endgame
            ? `Unused Rift Energy and Colossus claims turn into Rested at day's end (+10 each). A rested run spends ${REST_PER_RUN} to double its rewards.`
            : 'Rested builds up once the Rift Delve and Colossus Hunts open at level 30.'}
        </p>
      </div>
      {session && (
        <div class="bty-rest">
          <button
            class="btn btn--gold wide"
            disabled={!rest.ok}
            onClick={() => game && restDay(game)}
          >
            Rest until Day {a.day + 1}
          </button>
          {!rest.ok && <div class="bty-rest-why">{rest.reason}</div>}
        </div>
      )}
    </aside>
  );
}

function SealExchange() {
  const ctx = useCtx();
  if (!ctx) return null;
  const { c } = ctx;
  const earned = c.counters.sealsEarned ?? 0;
  const rep = repTier(earned);
  const shop = SHOPS.bounty_seals;
  return (
    <div class="bty-seals">
      <div class="bty-rep">
        <div>
          <span class="fx-h">Board standing</span>
          <div class="bty-rep-name">{rep.name}</div>
        </div>
        <div class="bty-rep-bar">
          <Bar
            value={earned - (REP_TIERS[rep.tier] ?? 0)}
            max={(rep.next ?? earned + 1) - (REP_TIERS[rep.tier] ?? 0)}
            tone="gold"
            label={rep.next ? `${earned}/${rep.next} seals earned` : `${earned} seals earned`}
          />
          <span class="fx-dim">
            {rep.next ? `Next: ${REP_NAMES[rep.tier + 1] ?? ''}` : 'Highest standing'}
          </span>
        </div>
        <Amount kind="bounty_seal" n={countOf(c, 'bounty_seal')} />
      </div>
      {shop && (
        <ShopView
          shop={shop}
          rep={rep.tier}
          repName={(t) => `${REP_NAMES[t] ?? 'higher'} standing`}
        />
      )}
    </div>
  );
}

/** Panel `bounty`: daily/weekly bounties, the day clock, rest and the seal exchange. */
export function BountyPanel() {
  const ctx = useCtx();
  const [tab, setTab] = useState<Tab>('daily');
  if (!ctx) return null;
  const { c } = ctx;
  const count = (k: BountyKind) => {
    const st = k === 'daily' ? c.daily : c.weekly;
    return `${st.bountiesDone.length}/${st.bounties.length}`;
  };
  const ids = tab === 'daily' ? c.daily.bounties : tab === 'weekly' ? c.weekly.bounties : [];
  return (
    <Window
      title="Bounty Board"
      width="min(1000px, 96vw)"
      height="min(660px, 88vh)"
      className="fx-win fx-bounty"
    >
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'daily'}
            onClick={() => setTab('daily')}
          >
            Daily <span class="bty-count">{count('daily')}</span>
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'weekly'}
            onClick={() => setTab('weekly')}
          >
            Weekly <span class="bty-count">{count('weekly')}</span>
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'seals'}
            onClick={() => setTab('seals')}
          >
            <Coin kind="bounty_seal" size={12} /> Seal Exchange
          </button>
        </div>
        <span class="fx-spacer" />
        <span class="bty-hint">
          Quill posts fresh notices every dawn. Claim here or at the board.
        </span>
      </div>
      <div class="bty-body">
        {tab === 'seals' ? (
          <SealExchange />
        ) : (
          <main class="bty-main fx-scroll">
            {ids.map((id) => (
              <BountyCard key={id} id={id} />
            ))}
          </main>
        )}
        <DaySide />
      </div>
      <TipLayer />
    </Window>
  );
}
